import * as React from "react";
import * as moment from "moment";
import {
    ALLIANZ_ESSENTIALS,
    AnnualPolicyInfo,
    CommonHelpers,
    INSTALMENT_STATUS_PENDING_PAYMENT,
    InstalmentsInfo,
    INTERACTION_PROCESS_MTA_NB_CHANGE_START_DATE,
    INTERACTION_PROCESS_MTC,
    INTERACTION_PROCESS_MTC_LV,
    INTERACTION_PROCESS_NB,
    INTERACTION_PROCESS_RENEWAL,
    INTERACTION_PROCESS_SUPPORT_PORTAL_MTC,
    Policy,
    POLICY_STATUS_MTC,
    POLICY_STATUS_NEW,
    PREMIUM_TYPE_ANNUAL,
    productHelper,
    Renewal,
} from "@ferry/journey-services";
import {resourcesHelper} from "@ferry/portal-core";
import {resolveClaimType, resolveFuelType, resolveTransmission} from "../../../../../utils/resolveLookupValues";
import {
    formatDate,
    formatDateTime,
    getCursorClassName,
    getIsOptionalExtraIncludedText,
    getLink
} from "../../../../../CommonFunctions";
import {Link} from "react-router-dom";
import {lazyInject} from "@athena-portal/core";
import {
    convertDate,
    DocumentListModal,
    getInteractionVehicleFuelType,
    getNCDEarnTypeWording,
    PolicySummaryPriceWidget
} from "@ferry/plugin-ferry-journey";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import {ListItem} from "@athena-portal/journey-model";
import {Types as JourneyCoreTypeDefs} from "@athena-portal/plugin-journey/dist/Types";
import {ILookupService} from "@athena-portal/plugin-journey";
import {ButtonLink} from "../../../../../components/navigation/ButtonLink";
import {Types} from "../../../../../Types";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";

interface IProps {
    policy: Policy;
    // Policy that is still active but is going to be replaced
    currentlyActivePolicy?: Policy;
    annualPolicyInfo: AnnualPolicyInfo;
    vehicleIndex: number;
    isOldPolicy?: boolean;
    renewalProposal?: Renewal;
    renewalEnabled: boolean;
    showMtcPrice: boolean
}

interface IState {
    lookup: Record<string, ListItem[]>;
    documentPathToSizeMaps: Array<Map<string, string>>;
    instalmentsInfo?: InstalmentsInfo;
    showChangedStartDatePopup: boolean;
    mtaOnEndDateOfPolicy: boolean;
}

const DriverDetail: React.FC<{
    interactionId: string, vehicleIndex: number, isMainDriver: boolean,
    isPolicyHolder: boolean, driverIndex?: number, disableLinks: boolean, canPerformMta: boolean
}> =
    ({interactionId, vehicleIndex, isMainDriver, isPolicyHolder, driverIndex, disableLinks, canPerformMta}) => {
        const presetDriverPath = isPolicyHolder ? "PolicyHolder" : String(driverIndex);

        return (
            <div className="additional-driver-buttons flex-wrap">
                <Link
                    id={`vehicle-${vehicleIndex}-edit-${isMainDriver ? "main-driver" : `additional-driver${driverIndex !== undefined ? `-${driverIndex}` : ""}`}`}
                    aria-label="View or edit driver"
                    to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditDriverDetails/presetDriver${presetDriverPath}/${interactionId}`,
                        disableLinks)}
                    className={getCursorClassName(disableLinks)}>
                    <button className="btn btn-primary edit-button edit-dashboard-button" disabled={!canPerformMta}>
                        View / edit driver details
                    </button>
                </Link>

                {!isPolicyHolder &&
                    <Link
                        id={`vehicle-${vehicleIndex}-remove-${isMainDriver ? "main-driver" : `additional-driver${driverIndex !== undefined ? `-${driverIndex}` : ""}`}`}
                        aria-label="View or edit driver"
                        to={getLink(`/journey/show/product/AnnualMotorInsurance/process/mtaRemoveDriver/removeDriver${String(driverIndex)}EntryPage/${interactionId}`,
                            disableLinks)}
                        className={getCursorClassName(disableLinks)}>
                        <button className="btn btn-blue-dashboard edit-button" disabled={!canPerformMta}>
                            Delete driver
                        </button>
                    </Link>
                }
            </div>
        );
    }

export class YourCarCoverPanel extends React.Component<IProps, IState> {

    private readonly policyToRender: Policy;
    private readonly vehicleToRender: any;
    private readonly cancelled: boolean;
    private readonly newPolicy: boolean;
    private readonly vehicleDrivers: any[] = [];
    private readonly mtaOnEndDateOfPolicy: boolean;

    private originalLatestQuoteDate: moment.Moment;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(JourneyCoreTypeDefs.LookupService)
    private lookupService: ILookupService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            lookup: {},
            documentPathToSizeMaps: [],
            showChangedStartDatePopup: false,
            mtaOnEndDateOfPolicy: false
        };
        this.toggleHasAlreadyChangedStartDate = this.toggleHasAlreadyChangedStartDate.bind(this);

        this.policyToRender = this.props.currentlyActivePolicy || this.props.policy;

        this.vehicleToRender = this.policyToRender.interaction.vehicles[0];

        const originalQuoteDateRaw = this.policyToRender.interaction.renewalDetails?.periodStartDate ?? this.policyToRender.interaction.factors.originalQuoteDate;
        this.originalLatestQuoteDate = moment.utc(originalQuoteDateRaw, moment.ISO_8601);
        this.cancelled = this.props.policy.status === POLICY_STATUS_MTC;
        this.newPolicy = this.props.policy.status === POLICY_STATUS_NEW;

        const allDrivers: any[] = [this.policyToRender.interaction.policyHolder].concat(this.policyToRender.interaction.additionalDrivers || []);
        if (this.vehicleToRender.driverIds) {
            this.vehicleDrivers = this.vehicleToRender.driverIds
                .map((driverId: string) => allDrivers.find((driver: any) => driverId === driver.personId!))
                .filter((driver: any) => driver !== undefined);
        }

        this.mtaOnEndDateOfPolicy = moment.utc(this.props.policy.endDate).isSame(moment.utc(this.props.annualPolicyInfo?.maxFutureMtaDate), 'day');
    }

    public async componentDidMount() {
        this.customerPortalService.getLastPolicyVersionInstalmentsInfo(this.policyToRender.policyNumber, false)
            .subscribe((instalmentsInfo: any) => {
                this.setState({instalmentsInfo});
            });

        this.fetchLookupValues("mileage");
        this.fetchLookupValues("claimType");
    }

    public render() {
        const {vehicleIndex} = this.props;
        const interaction: any = this.policyToRender.interaction;
        const coverTier = this.policyToRender.interaction.coverTier;
        const premiumType: string = interaction.factors.premiumType;
        const totalPaid: number = this.state.instalmentsInfo?.totalAmount ?? 0;
        const showPriceWidget = (!this.cancelled || this.props.showMtcPrice);

        const promotions: any = interaction.promotions;
        let annualPrice: number;
        let price: number | undefined;

        if (promotions && promotions.promotionsDetails.length > 0) {
            const isFreeInsurance: boolean = promotions.promotionsDetails[0].promotionType === "FREE_INSURANCE";

            if (isFreeInsurance) {
                annualPrice = 0;
            } else {
                annualPrice = this.newPolicy ? Math.max(0, interaction.factors.annualPremium - promotions.promotionsTotalValue) : interaction.factors.annualPremium;
            }
        } else {
            annualPrice = interaction.factors.annualPremium;
        }

        price = premiumType === PREMIUM_TYPE_ANNUAL ? annualPrice : this.getInstallmentPayment();

        return (
            <div className="quote-summary-panel">
                <div className="padding-32">
                    {showPriceWidget && <PolicySummaryPriceWidget
                        price={price}
                        premiumType={premiumType}
                        totalPaid={totalPaid}
                    />}
                    <div className="d-flex d-md-none align-i-center w-100 table-section-title">
                        <img className="mb-0 car-panel-icon"
                             src={`/${resourcesHelper.THEME_PATH}/img/car-cover.svg`}
                             alt="Cover icon" aria-label="Cover icon"/>
                        <h4 className="font-family-display font-weight-bold m-0">Policy details</h4>
                    </div>
                    <div className="d-flex flex-row w-100">
                        <div className="d-none d-md-flex flex-column">
                            <img className="mb-0 car-panel-icon"
                                 src={`/${resourcesHelper.THEME_PATH}/img/car-cover.svg`}
                                 alt="Cover icon" aria-label="Cover icon"/>
                        </div>
                        <div className="d-flex flex-column">
                            <h4 className="d-none d-md-flex font-family-display font-weight-bold m-0">Policy
                                details</h4>
                            {this.renderCoverDetails(vehicleIndex)}
                        </div>
                    </div>
                </div>
                <hr className="w-100 annual-motor-separator"/>
                <div className="car-infoo">
                    <div className="padding-32">
                        <div className="d-flex d-md-none align-i-center w-100 table-section-title">
                            <img className="mb-0 car-panel-icon"
                                 src={`/${resourcesHelper.THEME_PATH}/img/car-icon-panel.svg`}
                                 alt="Vehicle icon"/>
                            <h4 className="font-family-display font-weight-bold mb-0">Car details</h4>
                        </div>
                        <div className="d-flex flex-row w-100">
                            <div className="d-none d-md-flex flex-column">
                                <img className="mb-0 car-panel-icon"
                                     src={`/${resourcesHelper.THEME_PATH}/img/car-icon-panel.svg`}
                                     alt="Vehicle icon"/>
                            </div>
                            <div className="d-flex flex-column">
                                <h4 className="d-none d-md-flex font-family-display font-weight-bold mb-0">Car
                                    details</h4>
                                {this.renderCarDetails()}
                            </div>
                        </div>
                    </div>
                </div>
                <hr className="w-100 annual-motor-separator"/>
                <div className="drivers-details">
                    <div className="padding-32">
                        <div className="d-flex d-md-none align-i-center w-100 table-section-title">
                            <img className="mb-0 car-panel-icon" src={`/${resourcesHelper.THEME_PATH}/img/drivers.svg`}
                                 alt="Driver icon"/>
                            <h4 className="font-family-display font-weight-bold mb-0">Driver(s) details</h4>
                        </div>
                        <div className="d-flex flex-row w-100">
                            <div className="d-none d-md-flex flex-column">
                                <img className="mb-0 car-panel-icon"
                                     src={`/${resourcesHelper.THEME_PATH}/img/drivers.svg`} alt="Driver icon"/>
                            </div>
                            <div className="d-flex flex-column">
                                <div>
                                    <h4 className="d-none d-md-flex font-family-display font-weight-bold mb-0">Driver(s)
                                        details</h4>
                                    {this.renderDriverDetailSections(vehicleIndex)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <hr className="w-100 annual-motor-separator"/>
                <div className="section-summery">
                    <div className="padding-32">
                        <div className="d-flex d-md-none align-i-center w-100 table-section-title">
                            <img className="mb-0 car-panel-icon"
                                 src={`/${resourcesHelper.THEME_PATH}/img/documents.svg`} alt="Documents icon"/>
                            <h4 className="font-family-display font-weight-bold mb-0">Policy documents</h4>
                        </div>
                        <div className="d-flex flex-row w-100">
                            <div className="d-none d-md-flex flex-column">
                                <img className="mb-0 car-panel-icon"
                                     src={`/${resourcesHelper.THEME_PATH}/img/documents.svg`} alt="Documents icon"/>
                            </div>
                            <div className="d-flex flex-column">
                                <div>
                                    <h4 className="d-none d-md-flex font-family-display font-weight-bold mb-0">
                                        Policy documents
                                    </h4>
                                    <div className="mt-3">
                                        <DocumentListModal
                                            productName={productHelper.parseProductName(this.policyToRender.products[0])}
                                            coverTier={coverTier}
                                            policyDate={this.originalLatestQuoteDate.toDate()}
                                            isMLEVisible={this.policyToRender.interaction.optionalExtras.motorLegalExpenses}
                                            isAccordion={false}
                                        />
                                    </div>
                                    {!this.props.isOldPolicy && (
                                        <div className="additional-driver-buttons">
                                            <Link to={`/dashboard/pickMotorClaimKind/${this.props.policy.policyNumber}`}
                                                  id={`makeClaim${vehicleIndex}`}
                                                  className={`buttonLink`}>
                                                <button className="btn btn-blue-dashboard edit-button" type="button">
                                                    Make a claim
                                                </button>
                                            </Link>
                                            {this.renderCancelPolicyButton(vehicleIndex)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderCoverDetails(vehicleIndex: number) {
        const interaction: any = this.policyToRender.interaction;
        const premiumType: string = interaction.factors.premiumType;
        const closeBtn = <button className="btn-close" onClick={this.toggleHasAlreadyChangedStartDate}/>
        const process: string = interaction.process;
        const mtaNotPresent: boolean = process === INTERACTION_PROCESS_NB || process === INTERACTION_PROCESS_RENEWAL;
        const showChangeStartDateLink: boolean = false;
        const coverLevel = interaction.coverTier && interaction.coverTier === ALLIANZ_ESSENTIALS ? "Essentials" : "Plus";
        return (
                <ul className="list-unstyled mb-0">
                    <li>
                        <p id={`vehicle${vehicleIndex}-startDate`}><span
                            className="font-weight-bold">Cover start date: </span>
                            {this.renderPolicyStartDate(this.policyToRender)}
                            {showChangeStartDateLink && (interaction.process === INTERACTION_PROCESS_NB || interaction.process === INTERACTION_PROCESS_MTA_NB_CHANGE_START_DATE)
                                && moment().utc().isBefore(moment(interaction.headerInfo.startDate).utc()) &&
                                <Link
                                    className="text-decoration-underline cursor-pointer text-blue-1 muli-bold-16"
                                    to={getLink(`/journey/show/product/AnnualMotorInsurance/process/mtaNbChangeStartDate/presetPage/${this.props.policy.interaction._id}`, interaction.hasChangedStartDate)}
                                    onClick={this.toggleHasAlreadyChangedStartDate} id="changeStartDate">
                                    <button className="btn btn-blue-dashboard edit-button">
                                        Change start date
                                    </button>
                                </Link>}
                        </p>
                    </li>
                    {interaction.hasChangedStartDate && <li>
                        <Modal className="falcon-modal" isOpen={this.state.showChangedStartDatePopup}
                               toggle={this.toggleHasAlreadyChangedStartDate}>
                            <ModalHeader toggle={this.toggleHasAlreadyChangedStartDate} close={closeBtn}/>
                            <ModalBody className="pt-0 overflow-auto">
                                <img src={`/${resourcesHelper.THEME_PATH}/img/alert-red.svg`} alt="Alert icon"/>
                                <p className="d-inline mt-0 mb-2 ml-3 mr-3 font-weight-bold fs-24">
                                    Change of the policy start date
                                </p>
                                <p className="pt-3 mb-1">You have already changed your policy start date. Changing the
                                    start date is only allowed once. If the start date is still wrong, you'll need to
                                    cancel this policy and set up a new one.
                                </p>
                            </ModalBody>
                        </Modal>
                    </li>}
                    <li>
                        <p id={`vehicle${vehicleIndex}-endDate`}><span
                            className="font-weight-bold">Cover expiry date: </span>
                            {this.props.isOldPolicy ? `${formatDateTime(this.policyToRender.endDate)}`
                                : `${moment.utc(this.policyToRender.endDate, moment.ISO_8601).format("DD MMM YYYY")}, 23:59`}
                        </p>
                    </li>
                    <li>
                        <p><span className="font-weight-bold">Cover type:</span> Comprehensive</p>
                    </li>
                    <li>
                        <p><span className="font-weight-bold">Cover level:</span> {coverLevel}</p>
                    </li>
                    <li>
                        <p>
                            <span className="font-weight-bold">
                                Protected no claim discount:
                            </span> {interaction.policyHolder.ncd.doNotDisclose ? "Included" : "Not included"}
                        </p>
                    </li>
                    <li>
                        <p>
                            <span className="font-weight-bold">
                                Motor legal expenses:
                            </span> {getIsOptionalExtraIncludedText(interaction.coverTier, interaction.optionalExtras.motorLegalExpenses)}
                        </p>
                    </li>
                    <li>
                        <p>
                            <span className="font-weight-bold">
                                Guaranteed hire car:
                            </span> {getIsOptionalExtraIncludedText(interaction.coverTier, interaction.optionalExtras.courtesyCar)}
                        </p>
                    </li>
                    <li className="d-flex flex-column">
                        <p>{premiumType === PREMIUM_TYPE_ANNUAL && mtaNotPresent
                            ? '* includes any changes made during the policy year'
                            : '* includes any changes that you’ve made'}</p>
                        <p>{premiumType === PREMIUM_TYPE_ANNUAL
                            ? 'incl. Insurance Premium Tax'
                            : 'incl. Interest and Insurance Premium Tax'}</p>
                    </li>
                </ul>
        );
    }

    private renderCarDetails() {
        const vehicleExcesses: any = this.policyToRender.interaction.vehicles[0].annualVehicleExcess;
        const vehicleFuelTypeActual = getInteractionVehicleFuelType(this.vehicleToRender.fuelType, this.vehicleToRender.fuelTypeFull);
        const linkClass = `text-decoration-underline ${getCursorClassName(this.isMtaDisabled())} text-blue-1 muli-bold-16`;

        return (
            <React.Fragment>
                <div className="car-subject">
                    <div className="font-family-display font-weight-bold car-subject-title mb-3">
                        {`${this.vehicleToRender.make}, ${this.vehicleToRender.model}`}
                    </div>
                    <div className="number-plate-preview mb-3">{this.vehicleToRender.registrationNumber}</div>
                    <div>{`${this.vehicleToRender.year}, ${resolveTransmission(this.vehicleToRender.transmission)}, ${resolveFuelType(vehicleFuelTypeActual)}`}</div>
                    <div>{`Manufactured in ${this.vehicleToRender.year}`}</div>
                    <div>{`Purchased in ${convertDate(this.vehicleToRender.purchaseDate, "MMM YYYY")}`}</div>
                    <div className="text-center">
                        {!this.props.isOldPolicy && (!this.props.annualPolicyInfo?.maxMtaDateReached && !this.mtaOnEndDateOfPolicy) &&
                            <div className="additional-driver-buttons">
                                <Link id={`editCar1Details`} aria-label="Edit car details"
                                      to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaEditVehicleDetails/editCarEntryPage/${this.props.policy.interaction._id}`, this.isMtaDisabled())}
                                      className={`${linkClass}`}>
                                    <button className="btn btn-blue-dashboard edit-button"
                                            disabled={this.isMtaDisabled()}>
                                        Edit car
                                    </button>
                                </Link>
                                <Link id={`replaceCar1`} aria-label="Replace car"
                                      to={getLink(`/journey/init/product/AnnualMotorInsurance/process/mtaReplaceCar/preset/${this.props.policy.interaction._id}`, this.isMtaDisabled())}
                                      className={`${linkClass}`}>
                                    <button className="btn btn-white-dashboard edit-button"
                                            disabled={this.isMtaDisabled()}>
                                        Replace car
                                    </button>
                                </Link>
                            </div>}
                        {(this.props.annualPolicyInfo?.maxMtaDateReached || this.mtaOnEndDateOfPolicy) &&
                            <span>Edit / Replace car</span>}
                    </div>
                    <ul className="list-unstyled mb-0">
                        <li>
                            <p>
                                <span className="font-weight-bold">
                                    Annual mileage:
                                </span> {this.resolveLookupValue("mileage", this.vehicleToRender.mileage.toString())}
                            </p>
                        </li>
                        <li>
                            <p>
                                <span className="font-weight-bold">
                                    Postcode where the car is kept:
                                </span> {this.vehicleToRender.overnightLocationPostcode}
                            </p>
                        </li>
                        <li>
                            <p>
                                <span className="font-weight-bold">
                                    Car usage:
                                </span> {this.fetchLookupValue("carUsage", this.vehicleToRender.classOfUse)}
                            </p>
                        </li>
                    </ul>
                </div>
                <div className="excesses">
                    <div className="excesses-title mb-3">Excesses that apply</div>
                    <div className="mb-3">
                        <div>Accidental
                            damage: <strong>{CommonHelpers.getAmountWithoutDecimalDigits(vehicleExcesses.compulsoryExcess + vehicleExcesses.voluntaryExcess)} (this includes {CommonHelpers.getAmountWithoutDecimalDigits(vehicleExcesses.voluntaryExcess)} voluntary excess)</strong>
                        </div>
                        <div>Fire or
                            theft: <strong>{CommonHelpers.getAmountWithoutDecimalDigits(vehicleExcesses.compulsoryExcess + vehicleExcesses.voluntaryExcess)} (this includes {CommonHelpers.getAmountWithoutDecimalDigits(vehicleExcesses.voluntaryExcess)} voluntary excess)</strong>
                        </div>
                    </div>
                    <div className="mb-3">
                        <div className="font-weight-bold">Glass-only excess costs you less</div>
                        <div>
                            Glass repair excess: just <strong>{CommonHelpers.getAmountWithoutDecimalDigits(vehicleExcesses.windscreenRepairExcess)}</strong>
                        </div>
                        <div>
                            Glass replacement excess:
                            just <strong>{CommonHelpers.getAmountWithoutDecimalDigits(vehicleExcesses.windscreenReplacementExcess)}</strong>
                        </div>
                    </div>
                    <div>
                        <div className="font-weight-bold">Non-recommended repairer excess costs you more</div>
                        <div>
                            Non-recommended repairer excess:
                            <strong>
                                {CommonHelpers.getAmountWithoutDecimalDigits(vehicleExcesses.nonRecommendedRepairerExcess)}
                            </strong>
                        </div>
                        This is in addition to the excesses shown above if you choose a
                        repairer <strong>not</strong> on our list of recommended repairers.<br/>
                        This also applies to glass replacement but <strong>not</strong> glass-only repairs.
                    </div>
                </div>
                <div className="vehicle-drivers">
                    <div className="drivers-title mb-3">Drivers</div>
                    {this.renderVehicleDrivers()}
                </div>
            </React.Fragment>
        );
    }

    private toggleHasAlreadyChangedStartDate(): void {
        this.setState((state: IState) => ({
            showChangedStartDatePopup: !state.showChangedStartDatePopup
        }));
    }

    private hasDebt() {
        const {hasDebt, hasUnpaidChargebackInvoice, hasUnpaidChange} = this.props.annualPolicyInfo!;
        return hasDebt || hasUnpaidChargebackInvoice || hasUnpaidChange;
    }

    private isMtaDisabled() {
        return this.hasDebt() || this.hasMTC();
    }

    private hasMTC() {
        return this.policyToRender.interaction.process === INTERACTION_PROCESS_MTC
            || this.policyToRender.interaction.process === INTERACTION_PROCESS_MTC_LV
            || this.policyToRender.interaction.process === INTERACTION_PROCESS_SUPPORT_PORTAL_MTC;
    }

    private renderVehicleDrivers() {
        return this.vehicleDrivers.map((driver: any, index: number) => {
            return (
                <div key={index} className="mb-2">
                    {`${driver.firstName} ${driver.lastName}`} {driver.personId === this.vehicleToRender.mainDriverId &&
                    <span>(main driver)</span>}
                </div>
            );
        });
    }

    private renderDriverDetailSections(vehicleIndex: number) {
        let additionalDriverIndex = 0;
        return this.vehicleDrivers.map((driver: any, index: number) => {
            const isPolicyHolder = driver.personId === this.policyToRender.interaction.policyHolder.personId;
            const interaction: any = this.policyToRender.interaction;

            const driverIndexForLinks = isPolicyHolder ? undefined : additionalDriverIndex++;

            return (
                <ul key={index} className="drivers-section-spaceing list-unstyled">
                    <li>
                        <p className="font-weight-bold mr-1">
                            {`${driver.firstName} ${driver.lastName}`}
                        </p>
                    </li>

                    {this.renderDriverCommonSections(driver)}
                    {isPolicyHolder && this.renderMainDriverAdditionalFields(driver)}
                    {this.renderAdditionalDriverFields(driver)}

                    <DriverDetail
                        interactionId={interaction._id}
                        vehicleIndex={vehicleIndex}
                        isMainDriver={driver.personId === this.vehicleToRender.mainDriverId}
                        disableLinks={this.isMtaDisabled()}
                        canPerformMta={
                            !this.props.annualPolicyInfo.maxMtaDateReached &&
                            !this.isMtaDisabled()
                        }
                        isPolicyHolder={isPolicyHolder}
                        driverIndex={driverIndexForLinks}
                    />
                </ul>
            );
        });
    }

    private renderPolicyStartDate(policy: any) {
        if (moment.utc(policy.startDate, moment.ISO_8601).format("HH:mm") === '00:00') {
            return `${moment(policy.startDate, moment.ISO_8601).format("DD MMM YYYY")}, ${this.props.isOldPolicy ? `00:00` : `00:01`}`;
        } else {
            return moment(policy.startDate, moment.ISO_8601).format("DD MMM YYYY, HH:mm");
        }
    }

    private renderClaims(driver: any) {
        if (driver.hasClaims && driver.numberOfClaims > 0 && driver.claims?.length > 0) {
            return driver.claims.map((claim: any, index: any) => (
                <div key={index}>
                    {resolveClaimType(claim.claimType)}, {formatDate(claim.date, "MMM YYYY")}
                </div>
            ))
        } else {
            return <>None</>
        }
    }

    private renderMotoringConvictions(driver: any): React.ReactNode {
        if (driver?.dlnLookupData?.dlnLookupCallSucess) {
            return <>As per your DVLA records</>
        } else {
            if (driver.hasConvictions && driver.convictions?.length > 0) {
                return driver.convictions.map((conviction: any, index: any) => (
                    <React.Fragment key={index}>
                        {`${conviction.convictionCode}, ${convertDate(conviction.convictionDate, "MMM YYYY")}`}
                    </React.Fragment>
                ))
            } else {
                return <>None</>
            }
        }
    }

    private renderCancelPolicyButton(index: number): React.ReactNode {
        if (this.props.renewalProposal && this.props.renewalEnabled) {
            return (
                <ButtonLink
                    key={this.props.policy.policyNumber}
                    id={`cancelCar${index}Policy`}
                    className="buttonLink"
                    primary={false}
                    to={'/journey/init/product/AnnualMotorInsurance/process/renewal/renewalMTCEntryPage/' + this.props.renewalProposal?._id}
                    disabled={this.cancelled}>
                    Cancel policy
                </ButtonLink>
            )
        } else {
            return (
                <ButtonLink
                    key={this.props.policy.policyNumber}
                    id={`cancelCar${index}Policy`}
                    className="buttonLink"
                    primary={false}
                    to={`/journey/init/product/AnnualMotorInsurance/process/mtc/presetPage/${this.policyToRender.interaction?._id}`}
                    disabled={this.cancelled}>
                    Cancel policy
                </ButtonLink>
            )
        }
    }

    private getInstallmentPayment(): number {
        const nextInstalment = this.state.instalmentsInfo?.instalments.find(instalment => instalment.status === INSTALMENT_STATUS_PENDING_PAYMENT);
        return nextInstalment ? nextInstalment.amount : 0;
    }

    private resolveLookupValue(lookupName: string, lookupId: string): string {
        if (!lookupId || !this.state.lookup || !this.state.lookup[lookupName]) {
            return ("");
        }
        const listItem: ListItem | undefined = this.state.lookup[lookupName].find((item: ListItem) => `${item.id}` === `${lookupId}`);
        if (!listItem) {
            throw new Error(`Error: no item with id '${lookupId}' found in lookup '${lookupName}'`);
        }
        return listItem.value;
    }

    private fetchLookupValues(lookupName: string) {
        this.lookupService.fetchLookupValues(lookupName, "en-UK").subscribe((lookupValues: ListItem[]) => {
            this.setState(prevState => ({
                lookup: {
                    ...prevState.lookup,
                    [lookupName]: lookupValues
                }
            }));
        }, () => {
            throw new Error(`Error: failed to fetch lookup values for '${lookupName}' lookup`);
        });
    }

    private renderDriverCommonSections(driver: any) {
        return <>
            <li>
                <p>
                    <span className="font-weight-bold">
                        Date of birth:
                    </span> {convertDate(driver.dateOfBirth, "DD MMMM YYYY")}
                </p>
            </li>
            <li>
                <p>
                    <span className="font-weight-bold">
                        Driving licence type:
                    </span> {this.fetchLookupValue("licenceType", driver.licence.type.toString())}
                </p>
            </li>
            <li>
                <p><span className="font-weight-bold">Claims in the last 5 years:</span> {this.renderClaims(driver)}</p>
            </li>
            <li>
                <p>
                    <span className="font-weight-bold">
                        Criminal convictions:
                    </span> {CommonHelpers.booleanToText(driver.hasUnspentCriminalConvictions, "Yes", "None")}
                </p>
            </li>
            <li>
                <p>
                    <span className="font-weight-bold">
                        Motoring convictions:
                    </span> {this.renderMotoringConvictions(driver)}
                </p>
            </li>
        </>
    }

    private fetchLookupValue(fieldId: string, id: string): string {
        let value: string = "";

        this.lookupService.fetchLookupValues(fieldId, "en-UK").subscribe((lookupValues: ListItem[]) => {
            value = Object.values(lookupValues).filter(entry => entry.id === id)[0].value;
        });

        return value;
    }

    private renderNcdFields(driverNcdData: any) {
        if (driverNcdData) {
            return (
                <>
                    <li>
                        <p>
                            <span className="font-weight-bold">
                                No claim discount (NCD):
                            </span> {driverNcdData.doNotDisclose ? "Protected" : "Not protected"}
                        </p>
                    </li>
                    <li>
                        <p>
                            <span className="font-weight-bold">
                                Years of no claim discount (NCD):
                            </span> {driverNcdData.yearCount > 0 ? driverNcdData.yearCount : "None"}
                        </p>
                    </li>
                    <li>
                        <p>
                            <span className="font-weight-bold">
                                NCD earned:
                            </span> {getNCDEarnTypeWording(driverNcdData.earningType, this.policyToRender.interaction)}
                        </p>
                    </li>
                </>
            )
        } else {
            return <></>;
        }
    }

    private renderMainDriverAdditionalFields(driver: any) {
        const hasUnpaidChargebackInvoice: boolean = this.props.annualPolicyInfo!.hasUnpaidChargebackInvoice;
        const policyHolder = this.props.policy.policyHolder;

        return <>
            {this.renderNcdFields(driver.ncd)}
            <li className="d-flex flex-column">
                <p><span className="font-weight-bold">Address:</span> {driver.address.fullAddress}</p>
                <div>
                    {!this.props.isOldPolicy && (!this.props.annualPolicyInfo?.maxMtaDateReached && !this.mtaOnEndDateOfPolicy) && (
                        <div className="detail-key">
                            <Link
                                to={getLink('/journey/show/product/AnnualMotorInsurance/process/mtaChangeAddress/preset/' + this.policyToRender.interaction._id, hasUnpaidChargebackInvoice)}
                                className={`text-decoration-underline cursor-pointer text-blue-1 muli-bold-16 ${getCursorClassName(hasUnpaidChargebackInvoice)}`}
                                id="editAddress">
                                <button className="btn-text edit-phone-button">
                                    Change address
                                </button>
                            </Link>
                        </div>
                    )}
                    {!this.props.isOldPolicy && (this.props.annualPolicyInfo?.maxMtaDateReached || this.mtaOnEndDateOfPolicy) &&
                        <span className="detail-key change-address-link">Change address</span>}
                </div>
            </li>
            <li>
                <p><span className="font-weight-bold">Email:</span> {policyHolder.email}</p>
            </li>
        </>
    }

    private renderAdditionalDriverFields(driver: any) {
        const isMainDriver: boolean = driver.personId === this.vehicleToRender.mainDriverId;
        const isFutureStartDate = moment().isBefore(driver.startDate);

        if (!isMainDriver && isFutureStartDate) {
            return (
                <li>
                    <p className="w-100">* Cover start date: {moment(driver.startDate).format("YYYY-MM-DD")}</p>
                </li>
            )
        } else {
            return <></>;
        }
    }
}
